import { createElement, type HTMLAttributes } from 'react';
import { formatDuration, frameRateLabel, type Rational } from '@prism/contracts';

export function bytes(n: number | null | undefined): string {
  if (n == null) return '—';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  let v = n;
  let i = 0;
  while (v >= 1000 && i < units.length - 1) {
    v /= 1000;
    i++;
  }
  return `${v >= 100 || i === 0 ? Math.round(v) : v.toFixed(1)} ${units[i]}`;
}

/** A count rounded to three significant figures, with thousands separators: 12,345 → 12,300 (1.0.0-rc.13). */
export function roundCount(n: number): string {
  if (n < 1000) return String(Math.round(n));
  const p = 10 ** (Math.floor(Math.log10(n)) - 2);
  return (Math.round(n / p) * p).toLocaleString('en-US');
}

/** An estimated total (1.0.0-rc.13): "about 1.2 million", "about 245,000". */
export function aboutCount(n: number): string {
  if (n >= 1_000_000) return `about ${(Math.round(n / 100_000) / 10).toLocaleString('en-US')} million`;
  return `about ${roundCount(n)}`;
}

/** A count from a sample (1.0.0-rc.13): "≈ 12,300". */
export const approxCount = (n: number): string => `≈ ${roundCount(n)}`;

export function rate(bps: number): string {
  return `${bytes(bps)}/s`;
}

export function duration(ticks: number | null | undefined): string {
  return ticks == null ? '—' : formatDuration(ticks);
}

export function fps(r: Rational | null | undefined): string {
  return r ? `${frameRateLabel(r)} fps` : '—';
}

const rtf = new Intl.RelativeTimeFormat(undefined, { numeric: 'auto' });
export function relative(iso: string | null | undefined): string {
  if (!iso) return '—';
  const diff = (new Date(iso).getTime() - Date.now()) / 1000;
  const abs = Math.abs(diff);
  if (abs < 45) return 'just now';
  if (abs < 3600) return rtf.format(Math.round(diff / 60), 'minute');
  if (abs < 86400) return rtf.format(Math.round(diff / 3600), 'hour');
  if (abs < 86400 * 7) return rtf.format(Math.round(diff / 86400), 'day');
  return new Date(iso).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
}

export function dateTime(iso: string | null | undefined): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleString(undefined, { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit', second: '2-digit' });
}

/** A moment as relative time, with the exact date and time on hover; `as` picks the element (a table cell, say). */
export function When({ iso, as = 'span', ...rest }: { iso: string | null | undefined; as?: 'span' | 'td' | 'dd' } & HTMLAttributes<HTMLElement>) {
  return createElement(as, { ...rest, title: dateTime(iso) }, relative(iso));
}

/** Seconds as h:mm:ss. */
export function clock(seconds: number): string {
  return `${Math.floor(seconds / 3600)}:${String(Math.floor((seconds % 3600) / 60)).padStart(2, '0')}:${String(Math.floor(seconds % 60)).padStart(2, '0')}`;
}

/** A bit rate in bits per second: Mb/s with two decimals (a whole file), or whole kb/s (one stream). */
export function bitrate(bps: number | null | undefined, unit: 'Mb' | 'kb' = 'Mb'): string {
  if (!bps) return '—';
  return unit === 'Mb' ? `${(bps / 1e6).toFixed(2)} Mb/s` : `${Math.round(bps / 1000)} kb/s`;
}

export function elapsed(start: string | null, end: string | null): string {
  if (!start) return '';
  const ms = (end ? new Date(end).getTime() : Date.now()) - new Date(start).getTime();
  if (ms < 1000) return `${ms} ms`;
  if (ms < 60_000) return `${(ms / 1000).toFixed(1)} s`;
  return `${Math.floor(ms / 60_000)}m ${Math.round((ms % 60_000) / 1000)}s`;
}

export function resolution(w: number | null, h: number | null): string {
  if (!w || !h) return '—';
  const label = h >= 2160 ? 'UHD' : h >= 1080 ? 'HD 1080' : h >= 720 ? 'HD 720' : 'SD';
  return `${w}×${h} · ${label}`;
}

/** "Firefox 131 on macOS" from a user agent string; the raw string is in the tooltip. */
export function browserLabel(ua: string | null): string {
  if (!ua) return 'Unknown';
  const browser =
    /Edg\/(\d+)/.exec(ua) ? `Edge ${/Edg\/(\d+)/.exec(ua)![1]}` :
    /OPR\/(\d+)/.exec(ua) ? `Opera ${/OPR\/(\d+)/.exec(ua)![1]}` :
    /Firefox\/(\d+)/.exec(ua) ? `Firefox ${/Firefox\/(\d+)/.exec(ua)![1]}` :
    /Chrome\/(\d+)/.exec(ua) ? `Chrome ${/Chrome\/(\d+)/.exec(ua)![1]}` :
    /Version\/(\d+).*Safari/.exec(ua) ? `Safari ${/Version\/(\d+).*Safari/.exec(ua)![1]}` :
    /^(curl|node|undici|python)/i.test(ua) ? ua.split(/[\s/]/)[0]! : 'Browser';
  const os = /iPhone|iPad/.test(ua) ? 'iOS' : /Android/.test(ua) ? 'Android' : /Mac OS X|Macintosh/.test(ua) ? 'macOS' : /Windows/.test(ua) ? 'Windows' : /Linux/.test(ua) ? 'Linux' : null;
  return os ? `${browser} on ${os}` : browser;
}
